import { useMemo, useState } from "react";

import { parseISO, subDays, subMonths } from "date-fns";

import type { DateFilterValue } from "routes/invitations/components/DateFilter";
import { getStorageValue, setStorageValue } from "utils/storage/storageUtils";

interface RowWithDate {
  last_reminder_at?: string | null;
  sent_at?: string | null;
}

export const useDateFilter = <T extends RowWithDate>(rows: T[]) => {
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(
    () => getStorageValue("invitations_date_filter") ?? "all"
  );

  const handleFilterChange = (value: DateFilterValue) => {
    setDateFilter(value);
    setStorageValue("invitations_date_filter", value);
  };

  const getFilterDate = (): Date | null => {
    const now = new Date();
    switch (dateFilter) {
      case "last_30_days":
        return subDays(now, 30);
      case "last_3_months":
        return subMonths(now, 3);
      case "last_6_months":
        return subMonths(now, 6);
      case "last_12_months":
        return subMonths(now, 12);
      case "all":
        return null;
      default:
        return null;
    }
  };

  const filteredRows = useMemo(() => {
    const filterDate = getFilterDate();
    if (!filterDate) {
      return rows;
    }

    return rows.filter(row => {
      const dateToCheck = row.last_reminder_at || row.sent_at;
      if (!dateToCheck) {
        return false;
      }
      const rowDate = parseISO(dateToCheck);
      return rowDate >= filterDate;
    });
  }, [rows, dateFilter]);

  return {
    dateFilter,
    handleFilterChange,
    filteredRows,
  };
};
